import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useTrackContext } from '../../../Stage/floor/FloorFeed/TrackPositioner/TrackPositioner'
import { type Track, useTrack } from '../../../webrtc/webRTCRoom'
import { useSelectedAudioOutputDevice } from '../../../Devices/useDevices'
import { getRole } from '../../../global/blocs/roomInfo'

import {
  attachMimic,
  type MimicCrop,
  removeMimic,
  renderMimics,
  setMimicCrop,
} from './mimics'

import './VideoTrack.scss'

interface VideoTrackProps {
  track: Track
  // Improve performance by using canvas to display video instead of having to decode the video multiple times
  isMimic?: boolean
  // Reframing to replicate a layout's feed crop; overrides fit when set
  crop?: Partial<MimicCrop>
}

const VideoTrack = ({ track, crop, isMimic = false }: VideoTrackProps) => {
  if (isMimic) {
    return <MimicVideoTrack track={track} crop={crop} />
  }

  return <MainVideoTrack track={track} />
}

const MainVideoTrack = ({ track }: Pick<VideoTrackProps, 'track'>) => {
  const audioOutputDeviceId = useSelectedAudioOutputDevice()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [reactiveVideoRef, setReactiveVideoRef] =
    useState<HTMLVideoElement | null>(null)
  useTrack(track, reactiveVideoRef)

  const { trackSize, setTrackSize } = useTrackContext()

  const role = getRole()

  const isMuted =
    ((role === 'presenter' || role === 'producer') && !audioOutputDeviceId) ||
    !track.isEnabled

  useEffect(() => {
    if (isMuted) {
      renderMimics(track.mediaStream, videoRef, true)
    }
  }, [isMuted, track.mediaStream])

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      muted={isMuted}
      disablePictureInPicture
      ref={(ref) => {
        videoRef.current = ref
        setReactiveVideoRef(ref)
        renderMimics(track.mediaStream, videoRef)
        if (ref && ref.videoWidth > 0 && ref.videoHeight > 0) {
          if (
            trackSize.width !== ref.videoWidth ||
            trackSize.height !== ref.videoHeight
          ) {
            setTrackSize({ width: ref.videoWidth, height: ref.videoHeight })
          }
        }
      }}
      playsInline
      style={{
        display: 'block',
        height: '100%',
        width: '100%',
        objectFit: 'contain',
        '--videoHeight': reactiveVideoRef?.videoHeight || 720,
        '--videoWidth': reactiveVideoRef?.videoWidth || 1080,
      }}
    />
  )
}

const MimicVideoTrack = ({
  track,
  crop,
}: Pick<VideoTrackProps, 'track' | 'crop'>) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  const attachCanvasRef = useCallback(
    (ref: HTMLCanvasElement | null) => {
      if (ref) {
        attachMimic(track.mediaStream, ref)
        canvasRef.current = ref
      } else {
        removeMimic(track.mediaStream, canvasRef.current)
        canvasRef.current = null
      }
    },
    [track.mediaStream]
  )

  // Depend on the crop values, not the object, so a parent re-creating the
  // prop object doesn't trigger repaints
  const { zoom, centerX, centerY } = crop ?? {}
  useEffect(() => {
    setMimicCrop(canvasRef.current, {
      zoom: zoom ?? 0,
      centerX: centerX ?? 0,
      centerY: centerY ?? 0,
    })
  }, [zoom, centerX, centerY])

  return <canvas className="video-track-mimic" ref={attachCanvasRef} />
}

export { VideoTrack }
